import { type Interval } from "@tokenterminal/tt-analytics-api-types/dist/api/zoom"
import useMarketSectorsList, {
  type MarketSectorsListItem,
} from "modules/projects/EcosystemTab/useMarketSectorsList"
import { useMemo } from "react"
import { useMetricBreakdownBySectorSlug } from "./useMetricBreakdownBySectorSlug"
import { useProjectCountBySectorSlug } from "./useProjectCountBySectorSlug"

export type MarketSectorWithCountAndMetrics = MarketSectorsListItem & {
  projects_count?: number
  metrics: Record<string, { value: number | null; change: number | null }>
  percentOfTotal: number | null
}

/**
 * Returns a list of market sectors with added project count and metric data.
 * @example
 * const { data: marketSectors } = useMarketSectorsWithCountAndData(['fees', 'market_cap_fully_diluted'])
 */

export const useMarketSectorsWithCountAndMetrics = (
  metricId: string,
  interval: Interval
): MarketSectorWithCountAndMetrics[] => {
  // DATA FETCHING TODO: If this endpoint was updated to include project counts we could remove the useProjectCountBySectorSlug hook
  const { data: marketSectors = [] } = useMarketSectorsList()
  const projectCountBySectorSlug = useProjectCountBySectorSlug()
  const metrics = useMemo(() => [metricId], [metricId])
  const breakdownDataBySectorSlug = useMetricBreakdownBySectorSlug(
    metrics,
    interval
  )

  return useMemo(() => {
    if (!marketSectors) return []

    const total = marketSectors
      .filter((sector) => sector.slug !== "blockchains")
      .reduce((acc, sector) => {
        const metricData =
          breakdownDataBySectorSlug?.[sector.slug]?.[metricId] || {}
        return acc + (metricData.value || 0)
      }, 0)

    return marketSectors
      .filter((sector) => sector.slug !== "blockchains")
      .map((sector) => {
        const metricData =
          breakdownDataBySectorSlug?.[sector.slug]?.[metricId] || {}

        return {
          ...sector,
          projects_count: projectCountBySectorSlug?.[sector.slug] || 0,
          metrics: breakdownDataBySectorSlug?.[sector.slug] || {},
          percentOfTotal:
            metricData.value || metricData.value === 0
              ? metricData.value / total
              : null,
        }
      })
      .filter(
        (sector) =>
          sector.slug !== "blockchains" ||
          !["blockchains-l1", "blockchains-l2", "blockchains-l3"].includes(
            sector.slug
          )
      )
  }, [
    breakdownDataBySectorSlug,
    marketSectors,
    projectCountBySectorSlug,
    metricId,
  ])
}
